import { useUpdateScriptingBasic } from "@entities/brief/lib/hooks/scripting/useUpdateScriptingBasic";
import type { EditorHandle } from "@shared/ui/Editor/ui";
import { useEffect, useRef, type RefObject } from "react";
import { createConceptAutosave } from "./conceptAutosaveController";

type UseScriptConceptAutosaveParams = {
  scriptId: string;
  editorRef: RefObject<EditorHandle | null>;
  onSaved?: () => void;
};

export function useScriptConceptAutosave({
  scriptId,
  editorRef,
  onSaved,
}: UseScriptConceptAutosaveParams) {
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const scriptIdRef = useRef(scriptId);
  scriptIdRef.current = scriptId;
  const editorRefStable = editorRef;

  const updateBasicRef = useRef<(html: string) => void>(() => undefined);

  const autosaveRef = useRef<ReturnType<typeof createConceptAutosave> | null>(
    null
  );
  if (!autosaveRef.current) {
    autosaveRef.current = createConceptAutosave({
      getHtml: () =>
        editorRefStable.current?.getHTML({ syncTokens: true }) ?? "",
      persist: (html) => {
        const id = scriptIdRef.current;
        if (!id) {
          autosaveRef.current?.markSaveFailed();
          return;
        }
        updateBasicRef.current(html);
      },
    });
  }

  const { updateBasic, isUpdating } = useUpdateScriptingBasic(scriptId, {
    onSuccess: (variables) => {
      autosaveRef.current?.markSaveSucceeded(variables.concept ?? "");
      onSavedRef.current?.();
    },
  });

  updateBasicRef.current = (html: string) => {
    const id = scriptIdRef.current;
    if (!id) return;
    updateBasic({ scriptId: id, concept: html, silent: true });
  };

  const wasUpdatingRef = useRef(false);
  useEffect(() => {
    if (isUpdating) {
      wasUpdatingRef.current = true;
      return;
    }
    if (wasUpdatingRef.current) {
      wasUpdatingRef.current = false;
      autosaveRef.current?.markSaveFailed();
    }
  }, [isUpdating]);

  useEffect(() => () => autosaveRef.current?.dispose(), []);

  const autosave = autosaveRef.current;

  return {
    isUpdating,
    scheduleSave: autosave.scheduleSave,
    saveImmediately: autosave.saveNow,
    pauseAutosaveForMedia: autosave.pause,
    saveAfterMediaUpload: autosave.resumeAndSave,
    resetSnapshot: autosave.resetSnapshot,
  };
}
